import { PORTAL_NAME_MODAL_DEFINE } from 'components/common/PortalModal/config';
import {
  EN_LANG,
  HHMT_THEME,
  I18N_REDIRECTED,
  MAINTENANCE_HTML_CODE,
} from 'constants';
import { dispatchStore, stateStore } from 'store';
import { SET_PORTAL_MODAL, SET_SHOW_LOADING } from 'store/action';
import { domUtils, functionUtils, objectUtils } from 'utils';
import Cookies from './cookies';

const showPortalModalName = ({
  name,
  title,
  content,
  action,
  actionClose,
  modalStyles,
  modalOptions,
}) => {
  dispatchStore({
    type: SET_PORTAL_MODAL,
    payload: {
      isShowPortalModal: true,
      namePortalModal: name,
      ...(title && { title }),
      ...(content && { content }),
      ...(functionUtils.isFunction(action) && { action }),
      ...(functionUtils.isFunction(actionClose) && { actionClose }),
      ...(objectUtils.isObject(modalStyles) && { modalStyles }),
      ...(objectUtils.isObject(modalOptions) && { modalOptions }),
    },
  });
};

export const getMaintenanceHost = () => {
  const agentCode = process.env.REACT_APP_APP_NAME || HHMT_THEME;
  if (agentCode === HHMT_THEME)
    return process.env.REACT_APP_MAINTENANCE_HHMT_HOST;
  return process.env.REACT_APP_MAINTENANCE_REMOX_HOST;
};

export const getMaintenanceI18n = async () => {
  const url = `${getMaintenanceHost()}/i18n.json`;
  try {
    const response = await fetch(url);
    if (!response?.ok) {
      throw new Error(`Response status: ${response?.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(error?.message);
  }
  return null;
};

export const checkMaintenance = (error = {}) => {
  const { response } = error || {};
  const { status } = response || {};
  if (status !== MAINTENANCE_HTML_CODE) return;

  let detectMaintenanceInApp = false;
  const listener = () => {
    if (
      stateStore?.portalModal?.isShowPortalModal &&
      stateStore?.portalModal?.namePortalModal ===
        PORTAL_NAME_MODAL_DEFINE.MAINTENANCE_MODAL
    )
      return;
    detectMaintenanceInApp = true;
    showPortalModalName({
      isShow: true,
      name: PORTAL_NAME_MODAL_DEFINE.MAINTENANCE_MODAL,
    });
  };
  document.addEventListener('mousedown', listener);
  document.addEventListener('touchstart', listener);

  if (!detectMaintenanceInApp) {
    const locate =
      localStorage?.getItem(I18N_REDIRECTED) ||
      Cookies.get(I18N_REDIRECTED) ||
      domUtils.getQueryWithName('lang') ||
      EN_LANG;
    dispatchStore({ type: SET_SHOW_LOADING, payload: true });
    window.location.href = `${getMaintenanceHost()}?lang=${locate}`;
    return;
  }
};

// A transport/system failure (offline, DNS, dropped connection, CORS) — as
// opposed to a normal API error, which carries an HTTP `response`. Axios reports
// these with no `response` and `code === 'ERR_NETWORK'` / message
// 'Network Error'; `navigator.onLine === false` is the explicit offline signal.
export const isSystemNetworkError = (error = {}) => {
  const { response, code, message } = error || {};
  // A real HTTP response (any status, including maintenance 503) is not a
  // transport failure — leave it to checkMaintenance / the caller's handling.
  if (response) return false;
  if (typeof navigator !== 'undefined' && navigator?.onLine === false)
    return true;
  return code === 'ERR_NETWORK' || message === 'Network Error';
};

// Resolve the system-error modal message by error type. Returns null when the
// error must NOT surface the modal, so callers can fall through to their own
// handling. Raw strings are mocked pending i18n keys (TODO(i18n)).
//
// - transport/system failure (offline, dropped connection) -> 'Network error'
// - HTTP errors only when the caller opts in with httpErrors=true (so a backend
//   request was actually attempted; local UI/validation states never opt in):
//   - 503 -> null (checkMaintenance owns the maintenance redirect)
//   - 400 -> the API-provided message (generic fallback if absent)
//   - 500 / any other HTTP status -> 'label_system_error'
export const resolveSystemErrorContent = (
  error = {},
  { httpErrors = false } = {},
) => {
  // TODO(i18n): replace mocked copy with translation keys.
  if (isSystemNetworkError(error)) return 'label_content_network_lost';
  if (!httpErrors) return null;

  const { response } = error || {};
  const { status, data } = response || {};
  if (!status) return null;
  if (status === MAINTENANCE_HTML_CODE) return null;

  if (status === 400) return data?.message || 'label_system_error';
  return 'label_system_error';
};

export const resolveSystemErrorTitle = (
  error = {},
  { httpErrors = false } = {},
) => {
  // TODO(i18n): replace mocked copy with translation keys.
  if (isSystemNetworkError(error)) return 'label_network_lost';
  if (!httpErrors) return null;

  const { response } = error || {};
  const { status } = response || {};
  if (!status) return null;
  if (status === MAINTENANCE_HTML_CODE) return null;

  if (status === 400) return 'label_error_title';
  return 'label_error_title';
};

// Reusable across the repo: surface a single system-error inform modal for a
// transport or (opt-in) HTTP failure. Content is resolved per error type; the
// Close button label is translated inside SystemErrorModal (t('button_close')).
export const showSystemErrorModal = (content, actionClose, title) => {
  // Do not stack a second modal if one is already open.
  if (
    stateStore?.portalModal?.isShowPortalModal &&
    stateStore?.portalModal?.namePortalModal ===
      PORTAL_NAME_MODAL_DEFINE.SYSTEM_ERROR_MODAL
  )
    return;

  showPortalModalName({
    name: PORTAL_NAME_MODAL_DEFINE.SYSTEM_ERROR_MODAL,
    // Title intentionally omitted; the per-type raw text is the message body.
    content,
    // Optional per-caller Close handler (forwarded only when a function).
    actionClose,
    title,
  });
};

// Drop-in sibling of checkMaintenance for any API catch block: shows the
// reusable system-error modal when the error warrants it. By default only a
// transport/system failure triggers it; pass { httpErrors: true } from a catch
// where a backend request was actually attempted to also cover 400/500.
// Returns true when it handled the error (modal shown), false otherwise.
export const checkSystemError = (error = {}, actionClose, options = {}) => {
  const title = resolveSystemErrorTitle(error, options);
  const content = resolveSystemErrorContent(error, options);
  if (content == null) return false;
  showSystemErrorModal(content, actionClose, title);
  return true;
};
